// The bottom sheet's OPEN/CLOSE travel, as pre-integrated spring keyframes handed to the compositor.
// A CSS transition is wrong here twice over: Tailwind's `translate-y-*` drives the independent `translate`
// property, which is not reliably composited, and a transition starts when base-ui flips `[data-starting-style]`
// off — landing on the frames the menu's own commit and its first backdrop blur are already using.

import { useLayoutEffect, useRef, type RefObject } from 'react'
import { SPRING_OPTIONS, springValues } from '@/lib/spring-travel'
import { DISMISS_LANDED, dismissProgress } from '@/components/ui/drawer-snap-dismiss'
// The same release rule, on the same numbers, as a route swipe: one tuning for every gesture in the app.
import { TRANSITION_CONFIG } from '@/components/custom/route-transition/core/motion.config'

const { commitThreshold, flickVelocity } = TRANSITION_CONFIG

/** How raised the veil is right now, wherever it actually is — its resting dim comes from a CSS `calc()`. */
function backdropVeil(el: HTMLElement): number {
  return Number(getComputedStyle(el).opacity) || 0
}

/** Where a travel has visually got to, in px of downward offset — read from the pixels, so catching one is exact. */
function currentOffset(el: HTMLElement): number {
  const transform = getComputedStyle(el).transform
  if (transform === 'none') return 0
  try {
    return new DOMMatrixReadOnly(transform).f
  } catch {
    return 0
  }
}

function slide(el: HTMLElement, fromPx: number, toPx: number): Animation {
  return el.animate(
    springValues(fromPx, toPx).map((y) => ({ transform: `translate3d(0,${y}px,0)` })),
    SPRING_OPTIONS,
  )
}

/**
 * The veil: the backdrop's dim AND its blur on one scalar, so they arrive together. The blur has to be animated
 * EXPLICITLY — `opacity` does not fade a `backdrop-filter` (WebKit paints it at full strength however transparent
 * the element is), so fading the element only faded the dim and the blur snapped on at the start.
 */
function veil(el: HTMLElement, from: number, to: number, blurPx: number): Animation {
  return el.animate(
    springValues(from, to).map((level) => {
      const frame: Keyframe = { opacity: level.toFixed(3) }
      if (blurPx > 0) {
        const blur = `blur(${(Math.max(0, level) * blurPx).toFixed(2)}px)`
        frame.backdropFilter = blur
        frame.webkitBackdropFilter = blur
      }
      return frame
    }),
    SPRING_OPTIONS,
  )
}

/** The blur the backdrop rests at, in px — `0` where the engine has no backdrop-filter, so there's none to ramp. */
function restingBlur(el: HTMLElement): number {
  const style = getComputedStyle(el)
  const filter = style.getPropertyValue('backdrop-filter') || style.getPropertyValue('-webkit-backdrop-filter')
  return Number(/blur\(([\d.]+)px\)/.exec(filter)?.[1] ?? 0)
}

/**
 * Hold the backdrop at a given veil level across the first paint. WHILE PINNED, THE CSS TRANSITION IS OFF: every
 * value written here is one JS already knows the whole trajectory of, so a transition has nothing to smooth and
 * one thing to break — it chases each write instead of tracking it.
 */
function pinVeil(el: HTMLElement, level: number, blurPx: number): void {
  el.style.transitionProperty = 'none'
  el.style.opacity = String(level)
  if (blurPx > 0) {
    const blur = `blur(${(level * blurPx).toFixed(2)}px)`
    el.style.setProperty('backdrop-filter', blur)
    el.style.setProperty('-webkit-backdrop-filter', blur)
  }
}

/**
 * Hand the backdrop back to CSS, ACROSS ONE SUPPRESSED STYLE CHANGE. Dropping the pin outright reads as
 * "opacity 0 → 1" at the next style recalc and restarts the backdrop's own transition from zero — measured as the
 * veil dropping out and fading back in at the frame the travel finished, blur included (Chromium scales a
 * `backdrop-filter` by its element's opacity). Flushing while the transition is still off makes the resting value
 * the before-change style, so nothing has anywhere to transition from.
 */
function releaseVeil(el: HTMLElement): void {
  el.style.removeProperty('opacity')
  el.style.removeProperty('backdrop-filter')
  el.style.removeProperty('-webkit-backdrop-filter')
  // Take the style change here, while `transition-property` is still the `none` pinVeil wrote.
  void getComputedStyle(el).opacity
  el.style.removeProperty('transition-property')
}

/** Motion the user has asked not to see. The sheet still opens and closes — it just arrives. */
function motionSuppressed(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Take the sheet back from whatever is animating it, freezing it where it visually IS. Both halves have to be
 * caught: cancelling an animation reverts to the style underneath it, which for the veil is the level that travel
 * started at — the dim would snap back before the sheet had gone anywhere. `null` if nothing was moving.
 */
function catchTravel(
  animations: readonly Animation[],
  scroller: HTMLElement,
  backdrop: HTMLElement | null,
  blurPx: number,
): number | null {
  const moving = animations.some((animation) => animation.playState === 'running')
  const offset = moving ? currentOffset(scroller) : null
  const level = moving && backdrop ? backdropVeil(backdrop) : null
  for (const animation of animations) animation.cancel()
  if (offset !== null) scroller.style.transform = `translate3d(0,${offset}px,0)`
  if (level !== null && backdrop) pinVeil(backdrop, level, blurPx)
  return offset
}

interface SheetTravelArgs {
  /** The fixed scroll-snap container. The WHOLE sheet travels with it, so nothing inside it is transformed. */
  scrollerRef: RefObject<HTMLElement | null>
  backdropRef: RefObject<HTMLElement | null>
  /** The visible sheet, whose height is what a drag is measured against. */
  panelRef: RefObject<HTMLElement | null>
  /** The grab strip. A MOUSE drag on it is the only way to pull the sheet down without a native scroll. */
  handleRef: RefObject<HTMLElement | null>
  /** The sheet's own open state — `false` while base-ui keeps it mounted for the exit. */
  open: boolean
  onDismiss: () => void
}

export function useSheetTravel(args: SheetTravelArgs): void {
  const { scrollerRef, backdropRef, panelRef, handleRef, open, onDismiss } = args
  /** Where an interrupted travel was caught, for the next one to start from. */
  const caughtAt = useRef<number | null>(null)
  /** The backdrop's resting blur, cached the first time it is read — after that, the element carries a pinned
   *  value from a travel and reading it back would mistake THAT for the target. */
  const restingBlurPx = useRef<number | null>(null)
  /** Whatever currently drives the sheet, from either path, so each can take it from the other. */
  const driving = useRef<Animation[]>([])

  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    const backdrop = backdropRef.current
    // No `Element.animate`: the sheet appears at rest rather than animating from a position nothing can move it from.
    if (!scroller || typeof scroller.animate !== 'function' || motionSuppressed()) return

    // The scroller spans the viewport plus the bleed, so its own height is the distance that takes it fully off screen.
    const distance = scroller.offsetHeight
    const animations: Animation[] = []
    // Reopened mid-close (or closed mid-open): carry on from where the caught travel actually was.
    const from = caughtAt.current
    caughtAt.current = null

    if (backdrop && restingBlurPx.current === null) restingBlurPx.current = restingBlur(backdrop)
    const blurPx = restingBlurPx.current ?? 0

    if (open) {
      // Pre-paint: a just-created animation is play-pending, so the sheet would paint at rest and only then jump off.
      const startAt = from ?? distance
      const startVeil = backdrop && from !== null ? backdropVeil(backdrop) : 0
      scroller.style.transform = `translate3d(0,${startAt}px,0)`
      if (backdrop) pinVeil(backdrop, startVeil, blurPx)
      animations.push(slide(scroller, startAt, 0))
      if (backdrop) animations.push(veil(backdrop, startVeil, 1, blurPx))
      void Promise.all(animations.map((animation) => animation.finished))
        .then(() => {
          // Nothing may stay pinned on a resting sheet: a `fill: forwards` transform would hold the panel there.
          for (const animation of animations) animation.cancel()
          scroller.style.transform = ''
          if (backdrop) releaseVeil(backdrop)
        })
        .catch(() => {
          // A cancel rejects `finished`; that IS the interruptible path, and the exit branch takes over.
        })
    } else {
      // Dismissed by the FINGER: the scroll already carried the sheet off, and lifting the veil would flash the dim.
      const alreadyGone = dismissProgress(scroller.scrollTop, scroller.scrollHeight - scroller.clientHeight)
      if (alreadyGone >= DISMISS_LANDED) return
      animations.push(slide(scroller, from ?? 0, distance))
      // From wherever the veil actually is, so closing mid-drag (or mid-open) doesn't jump.
      if (backdrop) animations.push(veil(backdrop, backdropVeil(backdrop), 0, blurPx))
      // Nothing to clean up on arrival: base-ui waits for these (it awaits `getAnimations()`) and then unmounts.
    }

    driving.current = animations
    return () => {
      // Catch what is still travelling, so the next travel starts from the pixels on screen, not an endpoint.
      caughtAt.current = catchTravel(animations, scroller, backdrop, blurPx)
      driving.current = []
    }
  }, [open, scrollerRef, backdropRef])

  // A MOUSE drag on the handle: the dismiss gesture is a native scroll, which a mouse cannot perform, so on a narrow
  // desktop window the handle was inert. Touch is excluded — there the native scroll already owns the gesture.
  useLayoutEffect(() => {
    const handle = handleRef.current
    const scroller = scrollerRef.current
    if (!handle || !scroller) return

    let dragging = false
    let pointerId: number | null = null
    let startY = 0
    let baseOffset = 0
    let offset = 0
    let lastY = 0
    let lastT = 0
    let velocity = 0
    let panelHeight = 0

    const backdrop = () => backdropRef.current
    const blur = () => restingBlurPx.current ?? 0

    const paint = (): void => {
      scroller.style.transform = `translate3d(0,${offset}px,0)`
      const layer = backdrop()
      if (layer) pinVeil(layer, panelHeight > 0 ? Math.max(0, 1 - offset / panelHeight) : 1, blur())
    }

    const onPointerDown = (event: PointerEvent): void => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || dragging) return
      const panel = panelRef.current
      const layer = backdrop()
      panelHeight = panel ? panel.offsetHeight : scroller.offsetHeight
      if (layer && restingBlurPx.current === null) restingBlurPx.current = restingBlur(layer)
      // Catch a travel in flight so the sheet stops under the cursor instead of jumping to an endpoint.
      baseOffset = catchTravel(driving.current, scroller, layer, blur()) ?? currentOffset(scroller)
      driving.current = []
      dragging = true
      pointerId = event.pointerId
      startY = event.clientY
      lastY = event.clientY
      lastT = event.timeStamp
      velocity = 0
      offset = baseOffset
      // Otherwise the press starts a text selection that swallows the pointer stream mid-drag.
      event.preventDefault()
      document.body.style.userSelect = 'none'
    }

    const onPointerMove = (event: PointerEvent): void => {
      if (!dragging || event.pointerId !== pointerId) return
      const dt = event.timeStamp - lastT
      if (dt > 0) velocity = velocity * 0.6 + ((event.clientY - lastY) / dt) * 0.4
      lastY = event.clientY
      lastT = event.timeStamp
      // Down only: there is nothing above the resting position to drag into.
      offset = Math.max(0, baseOffset + (event.clientY - startY))
      paint()
    }

    const end = (): void => {
      if (!dragging) return
      dragging = false
      pointerId = null
      document.body.style.userSelect = ''
      // The route engine's release rule on its constants: a flick decides regardless of distance.
      const flicked = velocity > flickVelocity ? true : velocity < -flickVelocity ? false : undefined
      const dismissing = flicked ?? offset / Math.max(1, panelHeight) > commitThreshold
      if (dismissing) {
        // Hand the endpoint to the exit travel rather than animating here, so there is exactly one spring.
        caughtAt.current = offset
        onDismiss()
        return
      }
      const layer = backdrop()
      // Direct manipulation stands even under reduced motion, but the release must not animate there.
      if (motionSuppressed() || typeof scroller.animate !== 'function') {
        scroller.style.transform = ''
        if (layer) releaseVeil(layer)
        return
      }
      const springBack = [slide(scroller, offset, 0)]
      if (layer) springBack.push(veil(layer, backdropVeil(layer), 1, blur()))
      driving.current = springBack
      void Promise.all(springBack.map((animation) => animation.finished))
        .then(() => {
          for (const animation of springBack) animation.cancel()
          driving.current = []
          scroller.style.transform = ''
          if (layer) releaseVeil(layer)
        })
        .catch(() => {
          // Caught again mid-spring-back, or the sheet closed. Whoever cancelled it owns the sheet now.
        })
    }

    handle.addEventListener('pointerdown', onPointerDown)
    // On WINDOW, not the handle: a fast drag leaves the 20px strip, and a release outside it would never arrive.
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', end)
    window.addEventListener('pointercancel', end)
    window.addEventListener('blur', end)
    return () => {
      handle.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', end)
      window.removeEventListener('pointercancel', end)
      window.removeEventListener('blur', end)
      if (dragging) document.body.style.userSelect = ''
    }
  }, [handleRef, scrollerRef, backdropRef, panelRef, onDismiss])
}
